import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Modal, Space, Typography } from 'antd';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

const { Title, Text } = Typography;

const GLOBAL_NAV = [
    { keys: 'g a', label: 'Go to Accounts', target: '/brands-v2/accounts' },
    { keys: 'g o', label: 'Go to Orders', target: '/brands-v2/orders' },
    { keys: 'g b', label: 'Go to Brief', target: '/brands-v2/brief' },
    { keys: 'g p', label: 'Go to Privacy', target: '/brands-v2/privacy' },
    { keys: 'g r', label: 'Go to Product Review', target: '/brands-v2/review/products/in-review' },
    { keys: 'g c', label: 'Go to Creative Settings', target: '/brands-v2/creative-settings' },
    { keys: 'g k', label: 'Go to KCP', target: '/kcp' },
    { keys: 'g s', label: 'Go to System', target: '/system' },
    { keys: 'g n', label: 'Go to Notifications', target: '/notifications' }
];

const GLOBAL_META = [
    { keys: '⌘ K', label: 'Open command palette (search accounts / pages)' },
    { keys: '/', label: 'Focus search input on current page' },
    { keys: '?', label: 'Toggle this shortcuts dialog' },
    { keys: 'Escape', label: 'Close dialog / drawer' }
];

// Per-page shortcuts that are always listed in the help modal so admins can
// learn them even when they're not currently on that page. The handlers are
// wired locally by each page via useKeyboardShortcuts.
const PRODUCT_REVIEW_QUEUE = [
    { keys: 'j', label: 'Move focus down' },
    { keys: 'k', label: 'Move focus up' },
    { keys: 'Enter', label: 'Open focused product' },
    { keys: 'a', label: 'Approve focused product (in-review tab)' },
    { keys: 'r', label: 'Reject focused product (opens detail)' },
    { keys: 'x', label: 'Toggle selection on focused row' }
];

const PRODUCT_REVIEW_DETAIL = [
    { keys: 'a', label: 'Approve and auto-advance to next' },
    { keys: 'r', label: 'Reject and focus reason textarea' },
    { keys: '←', label: 'Previous product in queue' },
    { keys: '→', label: 'Next product in queue' }
];

const CUSTOM_CREATIVES_DETAIL = [
    { keys: '←', label: 'Previous campaign in queue' },
    { keys: '→', label: 'Next campaign in queue' }
];

const renderKeys = (keys) => (
    <Space size={4}>
        {keys.split(' ').map((k, i) => (
            <kbd key={`${k}-${i}`} className='shortcut-key'>
                {k === ' ' ? 'space' : k}
            </kbd>
        ))}
    </Space>
);

const Section = ({ title, items }) => (
    <div style={{ marginBottom: 20 }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
            {title}
        </Title>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr', rowGap: 8, columnGap: 16 }}>
            {items.map((item) => (
                <React.Fragment key={item.keys + item.label}>
                    <div>{renderKeys(item.keys)}</div>
                    <Text>{item.label}</Text>
                </React.Fragment>
            ))}
        </div>
    </div>
);

export const ShortcutsProvider = ({ children }) => {
    const navigate = useNavigate();
    const [helpOpen, setHelpOpen] = useState(false);

    const shortcuts = useMemo(
        () => [
            ...GLOBAL_NAV.map(({ keys, target }) => ({
                keys,
                handler: () => navigate(target)
            })),
            {
                keys: '?',
                handler: () => setHelpOpen((v) => !v)
            },
            {
                keys: '/',
                handler: () => {
                    const el = document.querySelector(
                        'input[type="search"], input.ant-input[placeholder*="earch"], input[placeholder*="earch"]'
                    );
                    if (el) {
                        el.focus();
                        el.select?.();
                    }
                }
            },
            {
                keys: 'Escape',
                handler: () => setHelpOpen(false)
            }
        ],
        [navigate]
    );

    useKeyboardShortcuts(shortcuts);

    return (
        <React.Fragment>
            {children}
            <Modal
                open={helpOpen}
                title='Keyboard shortcuts'
                onCancel={() => setHelpOpen(false)}
                footer={null}
                width={640}
            >
                <Section title='Navigation' items={GLOBAL_NAV} />
                <Section title='Product review — queue' items={PRODUCT_REVIEW_QUEUE} />
                <Section title='Product review — detail' items={PRODUCT_REVIEW_DETAIL} />
                <Section title='Custom Creatives review — detail' items={CUSTOM_CREATIVES_DETAIL} />
                <Section title='General' items={GLOBAL_META} />
            </Modal>
        </React.Fragment>
    );
};

// Global key styling uses the existing `kbd` rule in products-list SCSS;
// re-declare minimal fallback in case the help modal opens on other pages.
